import clsx from "clsx";
import { CircleAlert, CircleCheck, UploadIcon } from "lucide-react";
import { FC, useMemo, useState } from "react";
import { FormattedMessage } from "react-intl";
import {
  Document,
  DocumentPipelineStatus,
  DocumentStatus,
  UserRole,
} from "~/api/model";
import { Loader } from "~/components/Loader";
import { DisplayFor } from "~/components/permissions/DisplayFor";
import { DocumentPipelineStatistics } from "~/features/case/components/list/DocumentPipelineStatistics";
import { useProjectParams } from "~/helpers";
import { LoaderRays } from "./loader-rays";
import { Badge } from "./ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";

const pipelineStatusLabels: Record<string, DocumentPipelineStatus[]> = {
  "document.pipelineStatus.creatingPreview": [
    "preview_generation_started",
    "preview_generation_completed",
    "preview_generation_failed",
  ],
  "document.pipelineStatus.preprocessing": [
    "document_queued",
    "document_preprocess_started",
    "document_preprocess_completed",
    "document_preprocess_failed",
  ],
  "document.pipelineStatus.extractingText": [
    "content_extraction_started",
    "content_extraction_completed",
    "content_extraction_failed",
  ],
  "document.pipelineStatus.analyzingStructure": [
    "is_continuation_started",
    "is_continuation_completed",
    "is_continuation_failed",
    "is_continuation_ai_request_queried",
  ],
  "document.pipelineStatus.creatingSections": [
    "section_title_started",
    "section_title_completed",
    "section_title_ai_request_queried",
  ],
  "document.pipelineStatus.summarizing": [
    "summarization_started",
    "summarization_completed",
    "summarization_failed",
    "summarization_ai_request_queried",
  ],
  "document.pipelineStatus.categorizing": [
    "classification_started",
    "classification_completed",
    "classification_failed",
    "classification_ai_request_queried",
  ],
  "document.pipelineStatus.generatingCategoryDescriptions": [
    "category_short_description_started",
    "category_short_description_completed",
    "category_short_description_failed",
    "category_short_description_ai_request_queried",
  ],
  "document.pipelineStatus.generatingChatDescription": [
    "chat_short_description_started",
    "chat_short_description_completed",
    "chat_short_description_failed",
    "chat_short_description_ai_request_queried",
  ],
  "document.pipelineStatus.generatingSnapshot": [
    "chat_context_started",
    "chat_context_completed",
    "chat_context_failed",
  ],
  "document.pipelineStatus.definingTerms": [
    "term_definition_started",
    "term_definition_ai_request_queried",
  ],
  "document.pipelineStatus.recognizingTerms": [
    "terms_recognition_started",
    "terms_recognition_ai_request_queried",
  ],
  "document.pipelineStatus.processed": ["chat_context_completed"],
};

const getDocumentPipelineStatusLabel = (
  status?: DocumentPipelineStatus
): string => {
  if (!status) return "document.pipelineStatus.processing";

  // Find matching status group
  for (const [label, statuses] of Object.entries(pipelineStatusLabels)) {
    if (statuses.includes(status)) {
      return label;
    }
  }

  return "document.pipelineStatus.processing";
};

const categorizeError = (errorMessage?: string): string => {
  if (!errorMessage) return "document.error.unknown";

  const error = errorMessage.toLowerCase();
  if (error.includes("encrypt") || error.includes("password")) {
    return "document.error.encryption";
  }
  if (
    error.includes("corrupt") ||
    error.includes("invalid") ||
    error.includes("malformed")
  ) {
    return "document.error.corruption";
  }
  if (error.includes("timeout") || error.includes("time out")) {
    return "document.error.timeout";
  }
  if (
    error.includes("memory") ||
    error.includes("too large") ||
    error.includes("size")
  ) {
    return "document.error.memory";
  }
  return "document.error.unknown";
};

type DocumentStatusBadgeProps = {
  document: Document;
  errorMessage?: string;
};

const documentStatusMap = {
  [DocumentStatus.DRAFT]: {
    messageId: "document.status.DRAFT",
    className:
      "!border !border-border !text-muted-foreground !bg-card !shadow-none",
    icon: (
      <LoaderRays
        isShowPng
        isGray
        className="!w-[16px] !h-[16px] animate-spin"
      />
    ),
  },
  [DocumentStatus.COMPLETE]: {
    messageId: "document.status.COMPLETE",
    className: "!bg-green-500 !text-white border-none !ring-none !ring-0",
    icon: <CircleCheck className="!w-[16px] !h-[16px]" />,
  },
  [DocumentStatus.ERROR]: {
    messageId: "document.status.ERROR",
    className: "!bg-red-500 !text-white border-none !ring-none !ring-0",
    icon: <CircleAlert className="!w-[16px] !h-[16px]" />,
  },
  [DocumentStatus.UPLOADED]: {
    messageId: "document.status.UPLOADED",
    className:
      "!border !border-border !text-muted-foreground !bg-card !shadow-none",
    icon: <UploadIcon className="!w-[16px] !h-[16px]" />,
  },
  [DocumentStatus.PROCESSING]: {
    messageId: "", // This will be dynamically set based on pipeline status
    className:
      "!border !border-border !text-muted-foreground !bg-card !shadow-none",
    icon: (
      <Loader isShowPng isGray className="!w-[16px] !h-[16px] animate-spin" />
    ),
  },
};

export const DocumentStatusBadge: FC<DocumentStatusBadgeProps> = ({
  document,
  errorMessage,
}) => {
  const { organizationId, caseId } = useProjectParams();
  const [open, setOpen] = useState(false);

  const isQueued = !document.pageCount;
  const isError = document.status === DocumentStatus.ERROR;
  const loaderRays = (
    <LoaderRays isShowPng isGray className="!w-[16px] !h-[16px] animate-spin" />
  );

  const { status: documentStatus, pipelineStatus } = document;

  const statusConfig = useMemo(() => {
    const config = { ...documentStatusMap[documentStatus] };

    if (documentStatus === DocumentStatus.PROCESSING) {
      const pipelineMessageId = getDocumentPipelineStatusLabel(pipelineStatus);
      config.messageId = pipelineMessageId;
    }

    return config;
  }, [documentStatus, pipelineStatus]);

  return (
    <>
      <DisplayFor roles={[UserRole.SUPERADMIN]}>
        <Popover open={open} onOpenChange={setOpen}>
          <div
            onMouseEnter={() => setOpen(true)}
            onMouseLeave={() => setOpen(false)}
          >
            <PopoverTrigger className="outline-none">
              <Badge
                className={clsx(
                  statusConfig.className,
                  "!w-[24px] !h-[24px] p-0 flex rounded-full shadow-none justify-center items-center cursor-pointer"
                )}
              >
                {statusConfig.icon}
              </Badge>
            </PopoverTrigger>
          </div>
          <PopoverContent
            className="p-0 w-auto"
            side="top"
            align="start"
            onMouseEnter={() => setOpen(true)}
            onMouseLeave={() => setOpen(false)}
            sideOffset={5}
          >
            <DocumentPipelineStatistics
              documentId={document.id}
              organizationId={organizationId}
              projectId={caseId}
              pipelineStatus={document.pipelineStatus}
            />
          </PopoverContent>
        </Popover>
      </DisplayFor>
      <DisplayFor roles={[UserRole.OWNER, UserRole.MEMBER, UserRole.GUEST]}>
        <Tooltip delayDuration={400}>
          <TooltipTrigger className="cursor-default">
            <Badge
              className={clsx(
                statusConfig.className,
                "!w-[24px] !h-[24px] p-0 flex rounded-full shadow-none justify-center items-center"
              )}
            >
              {isQueued && !isError ? loaderRays : statusConfig.icon}
            </Badge>
          </TooltipTrigger>
          <TooltipContent>
            <FormattedMessage
              id={
                documentStatus === DocumentStatus.ERROR
                  ? categorizeError(errorMessage)
                  : documentStatus === DocumentStatus.PROCESSING &&
                      pipelineStatus
                    ? getDocumentPipelineStatusLabel(pipelineStatus)
                    : `document.status.${documentStatus}`
              }
              defaultMessage={documentStatus}
            />
          </TooltipContent>
        </Tooltip>
      </DisplayFor>
    </>
  );
};
